import { Card, Statistic, Progress, Typography, Skeleton } from 'antd';

const { Title } = Typography;

interface TargetData {
    target: number;
    achieved: number;
    remaining: number;
    percentage: number;
    budget?: number;
    utilized?: number;
}

interface HRStatsData {
    targets: {
        recruitment: Array<{
            jobTitle: {
                _id: string;
                name: string;
            };
            target: number;
            achieved: number;
            remaining: number;
            percentage: number;
            budget: number;
            utilized: number;
        }>;
        retention: TargetData;
        engagement: {
            activitiesPlanned: number;
            budgetAllocated: number;
            utilized: number;
            remaining: number;
            percentage: number;
        };
        training: {
            sessionsPlanned: number;
            budgetAllocated: number;
            utilized: number;
            remaining: number;
            percentage: number;
        };
        compliance: {
            complianceTarget: number;
            budgetAllocated: number;
            utilized: number;
            remaining: number;
            percentage: number;
        };
    };
    budget: {
        totalBudgetAllocated: number;
        totalBudgetUtilized: number;
        remaining: number;
        percentage: number;
    };
}

const HRTargetCard: React.FC<{ title: string; data?: TargetData | any; isMoney?: boolean; reverse?: boolean }> = ({ title, data, isMoney = false, reverse = false }) => {
    if (!data) return <Skeleton active paragraph={{ rows: 2 }} />;

    const percent = Math.min(data.percentage, 100);
    const isSuccess = reverse ? data.achieved <= data.target : data.percentage >= 100;

    return (
        <Card title={title} className="w-full">
            <div className="flex justify-between items-center mb-2">
                <Statistic title="Target" value={isMoney ? `₹${data.target?.toLocaleString?.()}` : reverse ? '≤ ' + data.target : data.target} />
                <Statistic title="Achieved" value={isMoney ? `₹${data.achieved?.toLocaleString?.()}` : data.achieved} />
            </div>
            <Progress
                percent={percent}
                status={isSuccess ? 'success' : 'exception'}
                strokeColor={isSuccess ? '#52c41a' : '#ff4d4f'}
            />
            <p className={`text-sm mt-2 ${data.remaining === 0 ? '' : reverse ? '' : 'text-red-500'}`}>
                Remaining: {isMoney ? `₹${data.remaining?.toLocaleString?.()}` : data.remaining}
            </p>
            {data.utilized !== undefined && data.budget !== undefined && (
                <div className='flex justify-between'>
                    <p className="text-sm text-gray-500 mt-1">Budget: ₹{data.budget.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Utilized: ₹{data.utilized.toLocaleString()}</p>
                </div>
            )}
        </Card>
    );
};

const HRStatsDashboard: React.FC<{ stats: HRStatsData | null }> = ({ stats }) => {
    if (!stats) return <Skeleton active paragraph={{ rows: 4 }} />;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Recruitment Cards */}
            {/* {stats.targets.recruitment.map((rec) => (
                <HRTargetCard
                    key={rec.jobTitle._id}
                    title={`Recruitment: ${rec.jobTitle.name}`}
                    data={rec}
                />
            ))} */}

            {/* Other HR Metrics */}
            <HRTargetCard title="Retention" data={stats.targets.retention} reverse={true} />

            <HRTargetCard
                title="Engagement"
                data={{
                    target: stats.targets.engagement.activitiesPlanned,
                    achieved: stats.targets.engagement.utilized,
                    remaining: stats.targets.engagement.remaining,
                    percentage: stats.targets.engagement.percentage,
                    budget: stats.targets.engagement.budgetAllocated,
                    utilized: stats.targets.engagement.utilized,
                }}
                isMoney={false}
            />

            <HRTargetCard
                title="Training"
                data={{
                    target: stats.targets.training.sessionsPlanned,
                    achieved: stats.targets.training.utilized,
                    remaining: stats.targets.training.remaining,
                    percentage: stats.targets.training.percentage,
                    budget: stats.targets.training.budgetAllocated,
                    utilized: stats.targets.training.utilized,
                }}
                isMoney={false}
            />

            <HRTargetCard
                title="Compliance"
                data={{
                    target: stats.targets.compliance.complianceTarget,
                    achieved: stats.targets.compliance.utilized,
                    remaining: stats.targets.compliance.remaining,
                    percentage: stats.targets.compliance.percentage,
                    budget: stats.targets.compliance.budgetAllocated,
                    utilized: stats.targets.compliance.utilized,
                }}
                isMoney={false}
            />

            {/* Total Budget Summary */}
            <HRTargetCard
                title="Total Budget"
                data={{
                    target: stats.budget.totalBudgetAllocated,
                    achieved: stats.budget.totalBudgetUtilized,
                    remaining: stats.budget.remaining,
                    percentage: stats.budget.percentage,
                }}
                isMoney={true}
            />
        </div>
    );
};

export default HRStatsDashboard;
